import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';

const CustomerInsightsDashboard = () => {
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [timeRange, setTimeRange] = useState('month');

  const satisfactionTrend = [
    { month: 'Jan', score: 82, responses: 234, nps: 42 },
    { month: 'Feb', score: 85, responses: 267, nps: 48 },
    { month: 'Mar', score: 88, responses: 298, nps: 52 },
    { month: 'Apr', score: 87, responses: 312, nps: 50 },
    { month: 'May', score: 91, responses: 345, nps: 58 },
    { month: 'Jun', score: 93, responses: 378, nps: 64 }
  ];

  const sentimentData = [
    { category: 'Very Positive', count: 219, percentage: 58, emoji: '😍', color: '#10b981' },
    { category: 'Positive', count: 106, percentage: 28, emoji: '😊', color: '#3b82f6' },
    { category: 'Neutral', count: 38, percentage: 10, emoji: '😐', color: '#f59e0b' },
    { category: 'Negative', count: 15, percentage: 4, emoji: '😔', color: '#ef4444' }
  ];

  const feedbackThemes = [
    { theme: 'Pricing Concerns', mentions: 89, sentiment: 'neutral', trend: 'down', priority: 'high' },
    { theme: 'Excellent Support', mentions: 156, sentiment: 'positive', trend: 'up', priority: 'maintain' },
    { theme: 'Feature Requests', mentions: 72, sentiment: 'neutral', trend: 'stable', priority: 'medium' },
    { theme: 'Easy to Use', mentions: 143, sentiment: 'positive', trend: 'up', priority: 'maintain' },
    { theme: 'Migration Issues', mentions: 34, sentiment: 'negative', trend: 'down', priority: 'high' },
    { theme: 'AI Assistance Helpful', mentions: 178, sentiment: 'positive', trend: 'up', priority: 'maintain' }
  ];

  const customerSegments = [
    { 
      segment: 'Enterprise',
      satisfaction: 95,
      count: 45,
      revenue: '$2.4M',
      churn: 2,
      growth: '+12%',
      color: '#10b981'
    },
    { 
      segment: 'Mid-Market',
      satisfaction: 92,
      count: 128,
      revenue: '$3.8M',
      churn: 5,
      growth: '+18%',
      color: '#3b82f6'
    },
    { 
      segment: 'Small Business',
      satisfaction: 88,
      count: 234,
      revenue: '$1.9M',
      churn: 8,
      growth: '+24%',
      color: '#f59e0b'
    },
    { 
      segment: 'Startup',
      satisfaction: 90,
      count: 167,
      revenue: '$1.2M',
      churn: 12,
      growth: '+31%',
      color: '#8b5cf6'
    }
  ];

  const recentFeedback = [
    {
      id: 1,
      customer: 'Acme Corp',
      rating: 5,
      sentiment: 'positive',
      comment: 'The AI assistance feature has been a game-changer for our sales team. Response times improved by 40%!',
      tags: ['AI Features', 'Performance'],
      agent: 'Sarah Mitchell',
      date: '2 hours ago'
    },
    {
      id: 2,
      customer: 'TechStart Inc',
      rating: 4,
      sentiment: 'positive',
      comment: 'Great platform overall. Would love to see more integration options with our existing tools.',
      tags: ['Integrations', 'Feature Request'],
      agent: 'James Chen',
      date: '5 hours ago'
    },
    {
      id: 3,
      customer: 'Global Solutions',
      rating: 5,
      sentiment: 'positive',
      comment: 'Migration was smooth and the support team was incredibly helpful throughout the process.',
      tags: ['Migration', 'Support'],
      agent: 'Emily Wong',
      date: '1 day ago'
    },
    {
      id: 4,
      customer: 'Innovation Labs',
      rating: 3,
      sentiment: 'neutral',
      comment: 'Good product but pricing seems high compared to competitors. Need better ROI visibility.',
      tags: ['Pricing', 'ROI'],
      agent: 'Alex Johnson',
      date: '1 day ago'
    },
    {
      id: 5,
      customer: 'DataFlow Systems',
      rating: 5,
      sentiment: 'positive',
      comment: 'Love the real-time hints! Our team closes deals 35% faster now.',
      tags: ['AI Features', 'Performance'],
      agent: 'Sarah Mitchell',
      date: '2 days ago'
    }
  ];

  const csatDrivers = [
    { factor: 'AI Assistance Quality', impact: 92, correlation: 0.89 },
    { factor: 'Response Time', impact: 88, correlation: 0.85 },
    { factor: 'Product Knowledge', impact: 85, correlation: 0.82 },
    { factor: 'Agent Empathy', impact: 91, correlation: 0.87 },
    { factor: 'Problem Resolution', impact: 94, correlation: 0.91 },
    { factor: 'Follow-up Quality', impact: 82, correlation: 0.78 }
  ];

  const getRatingEmoji = (rating) => {
    const emojis = ['😡', '😔', '😐', '😊', '😍'];
    return emojis[rating - 1] || '😐';
  };

  const getSentimentColor = (sentiment) => {
    const colors = {
      positive: '#10b981',
      neutral: '#f59e0b',
      negative: '#ef4444'
    };
    return colors[sentiment] || '#94a3b8';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return '📈';
    if (trend === 'down') return '📉';
    return '➖';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)',
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#e2e8f0',
      padding: '40px'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '48px'
      }}>
        <div style={{
          display: 'inline-block',
          padding: '8px 20px',
          background: 'rgba(16, 185, 129, 0.15)',
          borderRadius: '24px',
          fontSize: '13px',
          fontWeight: '700',
          color: '#10b981',
          marginBottom: '16px',
          textTransform: 'uppercase',
          letterSpacing: '0.1em'
        }}>
          Customer Intelligence
        </div>
        
        <h1 style={{
          fontSize: '56px',
          fontWeight: '900',
          marginBottom: '12px',
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '-0.03em'
        }}>
          Customer Satisfaction Analytics
        </h1>
        
        <p style={{
          fontSize: '20px',
          color: '#a5b4fc',
          maxWidth: '700px',
          lineHeight: '1.6'
        }}>
          Deep insights into customer sentiment, feedback patterns, and satisfaction drivers powered by AI analysis
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '24px',
        marginBottom: '40px'
      }}>
        {/* Overall CSAT */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.05) 100%)',
          borderRadius: '24px',
          padding: '32px',
          border: '2px solid rgba(16, 185, 129, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            fontSize: '140px',
            opacity: '0.08',
            transform: 'rotate(-15deg)'
          }}>
            😍
          </div>
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '14px',
              color: '#10b981',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '12px'
            }}>
              Overall CSAT
            </div>
            <div style={{
              fontSize: '72px',
              fontWeight: '900',
              color: '#10b981',
              lineHeight: '1',
              marginBottom: '12px',
              fontFamily: "'Space Mono', monospace"
            }}>
              93<span style={{ fontSize: '40px' }}>%</span>
            </div>
            <div style={{
              fontSize: '16px',
              color: '#10b981',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '24px' }}>📈</span>
              <span style={{ fontWeight: '600' }}>+8% vs last quarter</span>
            </div>
          </div>
        </div>

        {/* NPS Score */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.05) 100%)',
          borderRadius: '24px',
          padding: '32px',
          border: '2px solid rgba(59, 130, 246, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            fontSize: '140px',
            opacity: '0.08'
          }}>
            💯
          </div>
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '14px',
              color: '#3b82f6',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '12px'
            }}>
              NPS Score
            </div>
            <div style={{
              fontSize: '72px',
              fontWeight: '900',
              color: '#3b82f6',
              lineHeight: '1',
              marginBottom: '12px',
              fontFamily: "'Space Mono', monospace"
            }}>
              64
            </div>
            <div style={{
              fontSize: '16px',
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '24px' }}>🎯</span>
              <span style={{ fontWeight: '600' }}>Excellent tier</span>
            </div>
          </div>
        </div>

        {/* Response Rate */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.05) 100%)',
          borderRadius: '24px',
          padding: '32px',
          border: '2px solid rgba(245, 158, 11, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            fontSize: '140px',
            opacity: '0.08'
          }}>
            💬
          </div>
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '14px',
              color: '#f59e0b',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '12px'
            }}>
              Response Rate
            </div>
            <div style={{
              fontSize: '72px',
              fontWeight: '900',
              color: '#f59e0b',
              lineHeight: '1',
              marginBottom: '12px',
              fontFamily: "'Space Mono', monospace"
            }}>
              87<span style={{ fontSize: '40px' }}>%</span>
            </div>
            <div style={{
              fontSize: '16px',
              color: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '24px' }}>📊</span>
              <span style={{ fontWeight: '600' }}>378 responses</span>
            </div>
          </div>
        </div>

        {/* Churn Risk */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.05) 100%)',
          borderRadius: '24px',
          padding: '32px',
          border: '2px solid rgba(139, 92, 246, 0.3)',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            fontSize: '140px',
            opacity: '0.08'
          }}>
            🛡️
          </div>
          
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              fontSize: '14px',
              color: '#8b5cf6',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '12px'
            }}>
              Churn Risk
            </div>
            <div style={{
              fontSize: '72px',
              fontWeight: '900',
              color: '#8b5cf6',
              lineHeight: '1',
              marginBottom: '12px',
              fontFamily: "'Space Mono', monospace"
            }}>
              5.2<span style={{ fontSize: '40px' }}>%</span>
            </div>
            <div style={{
              fontSize: '16px',
              color: '#8b5cf6',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '24px' }}>📉</span>
              <span style={{ fontWeight: '600' }}>-2.1% improvement</span>
            </div>
          </div>
        </div>
      </div>

      {/* Satisfaction Trend & Sentiment */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Satisfaction Trend */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '24px',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>📈</span> Satisfaction Trajectory
          </h2>

          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={satisfactionTrend}>
              <defs>
                <linearGradient id="csat" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="nps" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="month" stroke="#a5b4fc" />
              <YAxis stroke="#a5b4fc" />
              <Tooltip 
                contentStyle={{
                  background: 'rgba(30, 27, 75, 0.95)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  backdropFilter: 'blur(10px)'
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="score" 
                stroke="#10b981" 
                strokeWidth={3}
                fill="url(#csat)"
                name="CSAT Score" 
              />
              <Area 
                type="monotone" 
                dataKey="nps" 
                stroke="#3b82f6" 
                strokeWidth={3}
                fill="url(#nps)"
                name="NPS" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sentiment Distribution */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '24px',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>😊</span> Sentiment
          </h2>

          {sentimentData.map((item, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: '20px'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '28px' }}>{item.emoji}</span>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    {item.category}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '8px'
                }}>
                  <span style={{
                    fontSize: '24px',
                    fontWeight: '900',
                    color: item.color,
                    fontFamily: "'Space Mono', monospace"
                  }}>
                    {item.percentage}%
                  </span>
                  <span style={{
                    fontSize: '14px',
                    color: '#94a3b8'
                  }}>
                    ({item.count})
                  </span>
                </div>
              </div>
              <div style={{
                width: '100%',
                height: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${item.percentage}%`,
                  height: '100%',
                  background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}dd 100%)`,
                  borderRadius: '6px',
                  transition: 'width 1.5s ease-out',
                  boxShadow: `0 0 15px ${item.color}60`
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Segments */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '24px',
        padding: '32px',
        marginBottom: '32px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)'
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: '800',
          marginBottom: '28px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>🎯</span> Customer Segments Performance
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '20px'
        }}>
          {customerSegments.map((seg, idx) => (
            <div
              key={idx}
              style={{
                background: `${seg.color}10`,
                border: `2px solid ${seg.color}40`,
                borderRadius: '20px',
                padding: '24px',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = `0 20px 40px ${seg.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: seg.color,
                marginBottom: '16px'
              }}>
                {seg.segment}
              </div>

              <div style={{
                fontSize: '48px',
                fontWeight: '900',
                color: seg.color,
                marginBottom: '8px',
                fontFamily: "'Space Mono', monospace"
              }}>
                {seg.satisfaction}%
              </div>

              <div style={{
                fontSize: '13px',
                color: '#94a3b8',
                marginBottom: '16px'
              }}>
                {seg.count} customers
              </div>

              <div style={{
                padding: '12px 0',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px'
                }}>
                  <span style={{ color: '#94a3b8' }}>Revenue:</span>
                  <span style={{ fontWeight: '700', color: seg.color }}>
                    {seg.revenue}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px'
                }}>
                  <span style={{ color: '#94a3b8' }}>Churn:</span>
                  <span style={{ fontWeight: '700' }}>
                    {seg.churn}%
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '13px'
                }}>
                  <span style={{ color: '#94a3b8' }}>Growth:</span>
                  <span style={{ fontWeight: '700', color: '#10b981' }}>
                    {seg.growth}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feedback Themes & Recent Feedback */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Feedback Themes */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '24px',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>💭</span> Feedback Themes
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {feedbackThemes.map((theme, idx) => (
              <div
                key={idx}
                style={{
                  padding: '20px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700'
                  }}>
                    {theme.theme}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '20px' }}>
                      {getTrendIcon(theme.trend)}
                    </span>
                    <span style={{
                      padding: '4px 12px',
                      background: `${getSentimentColor(theme.sentiment)}20`,
                      color: getSentimentColor(theme.sentiment),
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: '700',
                      textTransform: 'capitalize'
                    }}>
                      {theme.sentiment}
                    </span>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#94a3b8'
                  }}>
                    {theme.mentions} mentions
                  </div>
                  <div style={{
                    padding: '4px 12px',
                    background: theme.priority === 'high' 
                      ? 'rgba(239, 68, 68, 0.2)'
                      : 'rgba(59, 130, 246, 0.2)',
                    color: theme.priority === 'high' ? '#ef4444' : '#3b82f6',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '700',
                    textTransform: 'uppercase'
                  }}>
                    {theme.priority}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Feedback */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '24px',
          padding: '32px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)',
          maxHeight: '600px',
          overflowY: 'auto'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span>💬</span> Recent Feedback
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {recentFeedback.map((feedback) => (
              <div
                key={feedback.id}
                style={{
                  padding: '20px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '16px',
                  border: `1px solid ${getSentimentColor(feedback.sentiment)}40`
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700'
                  }}>
                    {feedback.customer}
                  </div>
                  <div style={{
                    fontSize: '24px'
                  }}>
                    {getRatingEmoji(feedback.rating)}
                  </div>
                </div>

                <div style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#cbd5e1',
                  marginBottom: '12px'
                }}>
                  "{feedback.comment}"
                </div>

                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  {feedback.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 10px',
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: '#3b82f6',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>Agent: {feedback.agent}</span>
                  <span>{feedback.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSAT Drivers */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '24px',
        padding: '32px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 25px 70px rgba(0, 0, 0, 0.4)'
      }}>
        <h2 style={{
          fontSize: '28px',
          fontWeight: '800',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>🔍</span> Satisfaction Drivers
        </h2>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={csatDrivers} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis type="number" stroke="#a5b4fc" />
            <YAxis dataKey="factor" type="category" stroke="#a5b4fc" width={150} />
            <Tooltip 
              contentStyle={{
                background: 'rgba(30, 27, 75, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '16px'
              }}
            />
            <Bar dataKey="impact" radius={[0, 8, 8, 0]}>
              {csatDrivers.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.impact > 90 ? '#10b981' : entry.impact > 85 ? '#3b82f6' : '#f59e0b'} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CustomerInsightsDashboard;